import {
  HoukatsuStatus,
  KYCStatus,
  NLPMigrationStatus,
} from 'apollo/queries/autogenerate/schemas';
import { PlusCardKind } from 'types/plus';

/**
 * Return true only for Classic consumers (no eKYC or Houkatsu), otherwise false
 * @param kycStatus user eKYC status
 */
export const isClassicUser = (kycStatus?: KYCStatus) => {
  return (
    kycStatus === KYCStatus.NEVER_APPLIED || kycStatus === KYCStatus.REJECTED
  );
};

/**
 * Return true only for eKYC approved and Houkatsu = cancelled or can_reapply
 * @param kycStatus user eKYC status
 * @param houkatsuStatus user Houkatsu status
 */
export const isClassicTypeB = (
  kycStatus?: KYCStatus,
  houkatsuStatus?: HoukatsuStatus
) => {
  return (
    kycStatus === KYCStatus.APPROVED &&
    (houkatsuStatus === HoukatsuStatus.CANCELED ||
      houkatsuStatus === HoukatsuStatus.CAN_REAPPLY)
  );
};

/**
 *  Return true only for Houkatsu approved consumers (eKYC and Houkatsu approved)
 * @param kycStatus user eKYC status
 * @param houkatsuStatus user houkatsu status
 */
export const isPlusUser = (
  kycStatus?: KYCStatus,
  houkatsuStatus?: HoukatsuStatus
) => {
  return (
    kycStatus === KYCStatus.APPROVED &&
    (houkatsuStatus === HoukatsuStatus.APPROVED ||
      houkatsuStatus === HoukatsuStatus.APPROVED_FOR_NLP)
  );
};

/**
 * Return true for simple classic users and for Houkatsu = cancelled or can_reapply.
 * NOTE : return false in case of any param is undefined.
 * @param kycStatus user eKYC status
 * @param houkatsuStatus user Houkatsu status
 */
export const isExtendedClassicUser = (
  kycStatus?: KYCStatus,
  houkatsuStatus?: HoukatsuStatus
) => {
  if (!!kycStatus && !!houkatsuStatus) {
    return (
      isClassicUser(kycStatus) || isClassicTypeB(kycStatus, houkatsuStatus)
    );
  }

  return false;
};

/**
 *  Return true only for Houkatsu approved consumers (eKYC and Houkatsu approved_for_NLP)
 * @param kycStatus user eKYC status
 * @param houkatsuStatus user houkatsu status
 */
export const isPlusNLPUser = (
  kycStatus?: KYCStatus,
  houkatsuStatus?: HoukatsuStatus
) => {
  return (
    kycStatus === KYCStatus.APPROVED &&
    houkatsuStatus === HoukatsuStatus.APPROVED_FOR_NLP
  );
};

/**
 *  Return true only for Houkatsu approved consumers (eKYC and Houkatsu approved_for_NLP), and NLPMigrationStatus AWAITING_CIC_RESULTS or AWAITING_PAC_ASSESSMENT
 * @param kycStatus user eKYC status
 * @param houkatsuStatus user houkatsu status
 * @param nlpMigrationStatus user NLPMigration status
 */
export const isPendingNLPUser = (
  kycStatus?: KYCStatus,
  houkatsuStatus?: HoukatsuStatus,
  nlpMigrationStatus?: NLPMigrationStatus
) => {
  return (
    kycStatus === KYCStatus.APPROVED &&
    houkatsuStatus === HoukatsuStatus.APPROVED &&
    (nlpMigrationStatus === NLPMigrationStatus.AWAITING_CIC_RESULTS ||
      nlpMigrationStatus === NLPMigrationStatus.AWAITING_PAC_ASSESSMENT)
  );
};

/**
 *  Return true only if Plus user approved or applying for Apple case
 *  and is not PlusNLPUser and has credit limit
 * @param plusCardKind user PlusCardKind
 * @param _isPlusNLPUser Houkatsu approved consumers (eKYC and Houkatsu approved_for_NLP)
 * @param plusLimit user credit limit
 */
export const isCreditLimitAvailable = (
  plusCardKind: PlusCardKind | null,
  _isPlusNLPUser: boolean,
  plusLimit?: unknown
) => {
  const isPlusCard =
    plusCardKind === PlusCardKind.APPLE_PENDING ||
    plusCardKind === PlusCardKind.APPLE_PAC_REQUIRED ||
    plusCardKind === PlusCardKind.APPLE_APPROVED ||
    plusCardKind === PlusCardKind.APPROVED;

  return Boolean(isPlusCard && !_isPlusNLPUser && plusLimit);
};
